/* renlace — theme JS. Copyright © 2026 renlace. All rights reserved. */

import {
  formatMoney,
  discountCheckoutUrl,
  stepperClamp,
  cartTotalPence,
  cartEyebrowText,
  bagCountText,
  lineQtyText,
  variantUrl,
} from './cart-utils.js';

const STANDARD_DELIVERY_PENCE = window.renlaceConfig?.standardDeliveryPence ?? 155;
const ROUTES = window.renlaceConfig?.routes ?? {};

// ── Mobile burger menu ───────────────────────────────────

const burger = document.querySelector('.site-nav__burger');
const drawer = document.querySelector('.site-nav__drawer');

burger?.addEventListener('click', () => {
  const isOpen = drawer.classList.toggle('is-open');
  burger.classList.toggle('is-open', isOpen);
  burger.setAttribute('aria-expanded', isOpen);
  burger.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
  drawer.setAttribute('aria-hidden', !isOpen);
});

// Close drawer when a link inside it is clicked
drawer?.querySelectorAll('a').forEach(a => {
  a.addEventListener('click', () => {
    drawer.classList.remove('is-open');
    burger?.classList.remove('is-open');
    burger?.setAttribute('aria-expanded', 'false');
    burger?.setAttribute('aria-label', 'Open menu');
    drawer.setAttribute('aria-hidden', 'true');
  });
});

// ── Accordion ────────────────────────────────────────────

document.querySelectorAll('.accordion__trigger').forEach(btn => {
  btn.addEventListener('click', () => {
    const item = btn.closest('.accordion__item');
    item.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', item.classList.contains('is-open'));
  });
});

// ── Quantity steppers (generic) ───────────────────────────

function initSteppers() {
  document.querySelectorAll('.stepper').forEach(stepper => {
    const minus = stepper.querySelector('[data-action="minus"]');
    const plus  = stepper.querySelector('[data-action="plus"]');
    const qty   = stepper.querySelector('.stepper__qty');
    const input = stepper.querySelector('input[type="hidden"]');

    if (!minus || !plus || !qty) return;

    const getVal = () => parseInt(qty.textContent, 10) || 1;

    minus.addEventListener('click', () => {
      const v = stepperClamp(getVal() - 1);
      qty.textContent = v;
      if (input) input.value = v;
      stepper.dispatchEvent(new CustomEvent('stepper:change', { detail: { qty: v }, bubbles: true }));
    });

    plus.addEventListener('click', () => {
      const v = getVal() + 1;
      qty.textContent = v;
      if (input) input.value = v;
      stepper.dispatchEvent(new CustomEvent('stepper:change', { detail: { qty: v }, bubbles: true }));
    });
  });
}

initSteppers();

// ── PDP ──────────────────────────────────────────────────

const pdpForm = document.querySelector('[data-pdp-form]');

if (pdpForm) {
  const swatches  = pdpForm.querySelectorAll('.colour-swatch');
  const headline  = document.querySelector('[data-pdp-headline]');
  const variantInput = pdpForm.querySelector('[name="id"]');
  const addBtn    = pdpForm.querySelector('[data-add-btn]');
  const notifyBtn = pdpForm.querySelector('[data-notify-btn]');
  const preorderBtn = pdpForm.querySelector('[data-preorder-btn]');
  const buyRow   = pdpForm.querySelector('[data-buy-row]');
  const oosActions = pdpForm.querySelector('[data-oos-actions]');
  const priceDisplay = pdpForm.querySelector('[data-add-btn-price]');

  // PDP gallery slideshow — synced with thumbs and counter
  const pdpSlideshowEl = document.querySelector('[data-pdp-slideshow]');
  const thumbs = Array.from(document.querySelectorAll('.pdp-gallery__thumb'));
  const counterEl = document.querySelector('[data-gallery-current]');
  let pdpSlideshow = null;

  if (pdpSlideshowEl) {
    pdpSlideshow = initSlideshow(pdpSlideshowEl, {
      autoplay: false,
      onChange: idx => {
        thumbs.forEach((t, i) => {
          if (i === idx) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
        if (counterEl) counterEl.textContent = idx + 1;
      },
    });
    pdpSlideshowEl.dataset.slideshowInit = '1';

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        pdpSlideshow?.goTo(parseInt(thumb.dataset.thumbIndex || '0', 10));
      });
    });

    const initialSwatch = pdpForm.querySelector('.colour-swatch[aria-checked="true"]');
    const initialIndex = parseInt(initialSwatch?.dataset.imageIndex || '0', 10);
    if (initialIndex > 0) pdpSlideshow?.goTo(initialIndex);
  }

  // price per unit from data attr (in pence) — updated on swatch change
  let unitPrice = parseInt(pdpForm.dataset.unitPrice || '1200', 10);

  // Qty stepper → update button price
  pdpForm.addEventListener('stepper:change', e => {
    if (priceDisplay) {
      const total = (unitPrice * e.detail.qty / 100).toFixed(2);
      priceDisplay.textContent = `£${total}`;
    }
  });

  // Colour swatch → switch variant
  swatches.forEach(swatch => {
    swatch.addEventListener('click', () => {
      swatches.forEach(s => s.setAttribute('aria-checked', 'false'));
      swatch.setAttribute('aria-checked', 'true');

      const color     = swatch.dataset.color;
      const variantId = swatch.dataset.variantId;
      const label     = swatch.dataset.label;
      const available = swatch.dataset.variantAvailable !== 'false';
      const newPrice  = parseInt(swatch.dataset.price || unitPrice, 10);
      unitPrice = newPrice;

      if (variantInput) variantInput.value = variantId;

      if (headline) {
        const parts = label.split(' // ');
        if (parts.length > 1) {
          headline.innerHTML = `<span style="white-space:nowrap">${parts[0]} //</span> <span style="white-space:nowrap">${parts[1]}.</span>`;
        } else {
          headline.textContent = label + '.';
        }
      }

      // Update document title — replace the first segment (variant/product name)
      // and preserve the rest of the pipe-separated suffix.
      const titleParts = document.title.split(' | ');
      if (titleParts.length > 1) {
        titleParts[0] = label;
        document.title = titleParts.join(' | ');
      } else {
        document.title = label;
      }

      const imageIndex = parseInt(swatch.dataset.imageIndex || '0', 10);
      pdpSlideshow?.goTo(imageIndex);

      history.replaceState(null, '', variantUrl(window.location, variantId));

      // availability state — swap between buy-row and OOS actions
      const hasPlan = swatch.dataset.variantHasPlan === 'true';
      if (buyRow) buyRow.hidden = !available;
      if (oosActions) oosActions.hidden = available;
      if (addBtn && available) {
        addBtn.innerHTML = `Add to bag &nbsp;·&nbsp; <span data-add-btn-price>${formatMoney(newPrice)}</span>`;
      }
      if (preorderBtn) preorderBtn.hidden = !hasPlan;
    });
  });

  // STOQ (Restock Rocket) — wire notify-me + pre-order to STOQ's API
  if (notifyBtn) {
    window.addEventListener('stoq:loaded', () => {
      notifyBtn.addEventListener('click', () => {
        window._RestockRocket?.openModal();
      });
    });
  }

  if (preorderBtn) {
    preorderBtn.addEventListener('click', async () => {
      const variantId = variantInput?.value;
      const plan = window._RestockRocket?.getSellingPlan?.(variantId);
      if (!plan?.enabled) return;
      const properties = window._RestockRocket?.getLineItemProperties?.(plan, variantId) || {};

      const originalText = preorderBtn.textContent;
      preorderBtn.disabled = true;
      preorderBtn.textContent = 'Adding…';

      try {
        const res = await fetch(`${ROUTES.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            selling_plan: plan.id,
            properties,
          }),
        });
        if (!res.ok) throw new Error('preorder add failed');
        const item = await res.json();
        await refreshMiniCart(item);
        openMiniCart();
      } catch (err) {
        console.error('Pre-order add to cart error', err);
        preorderBtn.textContent = 'Error — try again';
        setTimeout(() => { preorderBtn.textContent = originalText; }, 2500);
      } finally {
        preorderBtn.disabled = false;
        if (preorderBtn.textContent === 'Adding…') preorderBtn.textContent = originalText;
      }
    });
  }

  // Intercept add-to-cart → AJAX + mini-cart
  pdpForm.addEventListener('submit', async e => {
    e.preventDefault();
    const data = new FormData(pdpForm);

    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';

    try {
      const res = await fetch(`${ROUTES.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: data.get('id'),
          quantity: parseInt(data.get('quantity') || 1, 10),
        }),
      });

      if (!res.ok) throw new Error('add failed');

      const item = await res.json();
      await refreshMiniCart(item);
      openMiniCart();
    } catch (err) {
      console.error('Add to cart error', err);
      addBtn.textContent = 'Error — try again';
    } finally {
      addBtn.disabled = false;
      // restore button label
      const qty = parseInt(pdpForm.querySelector('.stepper__qty')?.textContent || 1, 10);
      const total = (unitPrice * qty / 100).toFixed(2);
      addBtn.innerHTML = `Add to bag &nbsp;·&nbsp; £${total}`;
    }
  });
}

// ── Mini-cart ─────────────────────────────────────────────

const miniCart     = document.querySelector('.mini-cart');
const backdrop     = document.querySelector('.mini-cart-backdrop');
const bagCountEls  = document.querySelectorAll('[data-bag-count]');

function openMiniCart() {
  if (!miniCart) return;
  miniCart.classList.add('is-open');
  backdrop?.classList.add('is-open');
}

function closeMiniCart() {
  if (!miniCart) return;
  miniCart.classList.remove('is-open');
  backdrop?.classList.remove('is-open');
}

miniCart?.querySelector('.mini-cart__dismiss')?.addEventListener('click', closeMiniCart);
backdrop?.addEventListener('click', closeMiniCart);

async function refreshMiniCart(lastAdded) {
  const cart = await fetch(`${ROUTES.cart}.js`).then(r => r.json());

  // update bag count
  bagCountEls.forEach(el => { el.textContent = bagCountText(cart.item_count); });

  if (!miniCart) return;

  // item row
  const itemEl = miniCart.querySelector('.mini-cart__item-name');
  const qtyEl  = miniCart.querySelector('.mini-cart__item-qty');
  const priceEl = miniCart.querySelector('.mini-cart__item-price');
  const subtotalEl = miniCart.querySelector('.mini-cart__subtotal-price');
  const thumbEl  = miniCart.querySelector('.mini-cart__thumb');

  if (itemEl && lastAdded)    itemEl.textContent  = lastAdded.variant_title || lastAdded.product_title;
  if (qtyEl  && lastAdded)    qtyEl.textContent   = lineQtyText(lastAdded.quantity);
  if (priceEl && lastAdded)   priceEl.textContent = formatMoney(lastAdded.final_line_price);

  if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

  // swap thumb bg if real image exists
  if (thumbEl && lastAdded?.image) {
    thumbEl.innerHTML = `<img src="${lastAdded.image}" alt="${lastAdded.title}" style="width:100%;height:100%;object-fit:cover;">`;
  }
}

// ── Cart page ─────────────────────────────────────────────

const cartPage = document.querySelector('[data-cart-page]');

if (cartPage) {
  const globalSkeletonSelector =
    '[data-cart-subtotal], [data-cart-shipping], [data-cart-total], [data-cart-eyebrow], [data-bag-count]';
  const setGlobalSkeletons = on => {
    document.querySelectorAll(globalSkeletonSelector)
      .forEach(el => el.classList.toggle('skeleton', on));
  };
  const setRowSkeleton = (row, on) => {
    row?.querySelector('.cart-row__total')?.classList.toggle('skeleton', on);
  };

  cartPage.addEventListener('stepper:change', async e => {
    const row = e.target.closest('[data-cart-row]');
    if (!row) return;

    const key = row.dataset.key;
    const qty = e.detail.qty;

    setGlobalSkeletons(true);
    setRowSkeleton(row, true);
    try {
      await fetch(`${ROUTES.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      await updateCartTotals();
    } finally {
      setGlobalSkeletons(false);
      setRowSkeleton(row, false);
    }
  });

  cartPage.querySelectorAll('[data-remove]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const row = btn.closest('[data-cart-row]');
      const key = row.dataset.key;

      setGlobalSkeletons(true);
      try {
        await fetch(`${ROUTES.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        row.remove();
        await updateCartTotals();
      } finally {
        setGlobalSkeletons(false);
      }
    });
  });
}

async function updateCartTotals() {
  const cart = await fetch(`${ROUTES.cart}.js`).then(r => r.json());

  bagCountEls.forEach(el => { el.textContent = bagCountText(cart.item_count); });

  const subtotalEl = document.querySelector('[data-cart-subtotal]');
  const shippingEl = document.querySelector('[data-cart-shipping]');
  const totalEl    = document.querySelector('[data-cart-total]');
  const checkoutBtn = document.querySelector('[data-checkout-btn]');
  const eyebrowEl  = document.querySelector('[data-cart-eyebrow]');

  if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

  cart.items.forEach(item => {
    const row = document.querySelector(`[data-cart-row][data-key="${item.key}"]`);
    const lineTotalEl = row?.querySelector('.cart-row__total');
    if (lineTotalEl) lineTotalEl.textContent = formatMoney(item.final_line_price);
  });

  if (shippingEl) shippingEl.textContent = formatMoney(STANDARD_DELIVERY_PENCE);
  if (totalEl) totalEl.textContent = formatMoney(cartTotalPence(cart.total_price, STANDARD_DELIVERY_PENCE));

  if (checkoutBtn) {
    if (cart.item_count === 0) {
      checkoutBtn.setAttribute('aria-disabled', 'true');
      checkoutBtn.setAttribute('tabindex', '-1');
    } else {
      checkoutBtn.removeAttribute('aria-disabled');
      checkoutBtn.removeAttribute('tabindex');
    }
  }

  if (eyebrowEl) eyebrowEl.textContent = cartEyebrowText(cart.item_count);

  // Hide/show empty state
  const emptyEl = document.querySelector('[data-cart-empty]');
  const itemsEl = document.querySelector('[data-cart-items]');
  if (cart.item_count === 0) {
    emptyEl?.style.setProperty('display', 'block');
    itemsEl?.style.setProperty('display', 'none');
  }
}

// ── Cart discount code ────────────────────────────────

const discountInput    = document.querySelector('[data-discount-input]');
const discountApply    = document.querySelector('[data-discount-apply]');
const discountFeedback = document.querySelector('[data-discount-feedback]');
const checkoutAnchor   = document.querySelector('[data-checkout-btn]');

if (discountApply && discountInput && checkoutAnchor) {
  const applyCode = () => {
    const raw = discountInput.value;
    if (!raw || !raw.trim()) return;
    const code = raw.trim().toUpperCase();

    checkoutAnchor.href = discountCheckoutUrl(raw, ROUTES.checkout);

    if (discountFeedback) {
      discountFeedback.textContent = `"${code}" — will be applied at checkout.`;
      discountFeedback.style.display = '';
    }

    discountApply.textContent = 'Applied ✓';
    setTimeout(() => { discountApply.textContent = 'Apply'; }, 2500);
  };

  discountApply.addEventListener('click', applyCode);
  discountInput.addEventListener('keydown', e => {
    if (e.key === 'Enter') { e.preventDefault(); applyCode(); }
  });
}

// ── Predictive search (search page) ──────────────────────

const searchInput = document.querySelector('[data-search-input]');
const searchResults = document.querySelector('[data-search-results]');

if (searchInput && searchResults) {
  let searchTimer;

  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimer);
    const q = searchInput.value.trim();
    if (q.length < 2) return;

    searchTimer = setTimeout(async () => {
      try {
        const res  = await fetch(`${ROUTES.search}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`);
        const data = await res.json();
        renderPredictive(data.resources?.results?.products ?? [], q);
      } catch {}
    }, 350);
  });

  function renderPredictive(products, q) {
    if (!products.length) {
      searchResults.innerHTML = `
        <div class="search-empty">
          <div class="eyebrow" style="margin-bottom:12px">No results for "${q}"</div>
          <p style="font-size:14px;color:var(--ink-2);margin:0 0 24px">Try a different word, or browse the full collection.</p>
          <a href="${ROUTES.allProducts}" class="btn">Shop all laces &nbsp;→</a>
        </div>`;
      return;
    }

    const cards = [];
    products.forEach(product => {
      const variants = product.variants?.length
        ? product.variants
        : [{ title: product.title, url: product.url, featured_image: product.featured_image }];
      variants.forEach(variant => {
        const tone  = (variant.title ?? '').toLowerCase();
        const bg    = tone.includes('white') ? 'var(--paper-3)' : 'var(--paper-2)';
        const img   = variant.featured_image?.url ?? product.featured_image?.url ?? product.image ?? '';
        const url   = variant.url ?? product.url;
        const price = product.price;
        cards.push(`
          <article>
            <a href="${url}" style="display:block;text-decoration:none;color:inherit">
              <div class="plp-card__image" style="background:${bg}">
                ${img ? `<img src="${img}" alt="${variant.title}" width="600" height="600" loading="lazy" class="product-image">` : ''}
                <span class="stamp-outline plp-card__stamp">N°01</span>
              </div>
              <div class="plp-card__meta">
                <div>
                  <div style="font-family:var(--display);font-size:26px">${variant.title}</div>
                  <div class="eyebrow" style="margin-top:4px">Slim · for trainers</div>
                </div>
                <span class="mono" style="font-size:13px">${price}</span>
              </div>
            </a>
            <a href="${url}" class="btn btn--outline" style="width:100%;margin-top:14px;font-size:13px">View &nbsp;→</a>
          </article>`);
      });
    });

    const count = cards.length;
    searchResults.innerHTML = `
      <div class="eyebrow search-results-count" style="margin-bottom:24px">
        ${count} ${count === 1 ? 'result' : 'results'} for "${q}"
      </div>
      <div class="search-grid">${cards.join('')}</div>`;
  }
}

// ── Slideshow ────────────────────────────────────────────

function initSlideshow(root, opts = {}) {
  const { autoplay = false, autoplayMs = 4000, onChange } = opts;
  const track  = root.querySelector('[data-slideshow-track]');
  const real   = Array.from(root.querySelectorAll('[data-slideshow-slide]'));
  const dots   = Array.from(root.querySelectorAll('[data-slideshow-dot]'));
  const arrows = Array.from(root.querySelectorAll('[data-slideshow-arrow]'));
  const count  = real.length;
  if (!track || count === 0) return null;

  if (count === 1) {
    arrows.forEach(a => a.hidden = true);
    dots.forEach(d => d.hidden = true);
    return { goTo: () => {}, next: () => {}, prev: () => {} };
  }

  // Clone slides: prepend a full set, append a full set → [clones | real | clones]
  real.forEach(s => track.appendChild(s.cloneNode(true)));
  for (let i = real.length - 1; i >= 0; i--) {
    track.insertBefore(real[i].cloneNode(true), track.firstChild);
  }

  let current = count; // first real slide
  let jumping = false;
  let timer;
  let arrowTimer;

  function setPos(idx, animate) {
    if (!animate) track.style.transition = 'none';
    track.style.transform = `translateX(-${idx * 100}%)`;
    if (!animate) {
      track.offsetHeight;
      track.style.transition = '';
    }
  }

  function realIndex(idx) { return ((idx - count) % count + count) % count; }

  function goTo(n) {
    if (jumping) return;
    // Hidden tabs keep ticking autoplay but run no transitions, so transitionend
    // never wraps current — re-base onto the real slides before running off the clones.
    if (n < 0 || n >= count * 3) {
      const wrapped = count + realIndex(current);
      n += wrapped - current;
      current = wrapped;
      setPos(current, false);
    }
    if (dots.length) dots[realIndex(current)].classList.remove('is-active');
    current = n;
    const ri = realIndex(current);
    if (dots.length) dots[ri].classList.add('is-active');
    setPos(current, true);
    onChange?.(ri);
  }

  track.addEventListener('transitionend', () => {
    if (current < count) {
      jumping = true; current += count; setPos(current, false); jumping = false;
    } else if (current >= count * 2) {
      jumping = true; current -= count; setPos(current, false); jumping = false;
    }
  });

  setPos(current, false);

  function start() { if (autoplay) timer = setInterval(() => goTo(current + 1), autoplayMs); }
  function stop()  { clearInterval(timer); }
  function reset() { stop(); start(); }

  root.querySelector('[data-slideshow-arrow="prev"]')?.addEventListener('click', () => { goTo(current - 1); reset(); });
  root.querySelector('[data-slideshow-arrow="next"]')?.addEventListener('click', () => { goTo(current + 1); reset(); });

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      const diff = i - realIndex(current);
      if (diff !== 0) { goTo(current + diff); reset(); }
    });
  });

  if (autoplay) {
    function showArrows() {
      arrows.forEach(a => a.classList.add('is-visible'));
      clearTimeout(arrowTimer);
      arrowTimer = setTimeout(() => arrows.forEach(a => a.classList.remove('is-visible')), 2000);
    }
    root.addEventListener('mousemove', showArrows);
    root.addEventListener('mouseenter', () => { stop(); showArrows(); });
    root.addEventListener('mouseleave', () => { arrows.forEach(a => a.classList.remove('is-visible')); start(); });
  }

  let touchStartX = 0;
  root.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
  root.addEventListener('touchend', e => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) < 40) return;
    goTo(dx < 0 ? current + 1 : current - 1);
    reset();
  }, { passive: true });

  start();

  return {
    goTo:  i => { const diff = i - realIndex(current); if (diff !== 0) { goTo(current + diff); reset(); } },
    next:  () => { goTo(current + 1); reset(); },
    prev:  () => { goTo(current - 1); reset(); },
  };
}

document.querySelectorAll('[data-slideshow]').forEach(el => {
  if (el.dataset.slideshowInit) return;
  initSlideshow(el, { autoplay: el.hasAttribute('data-slideshow-autoplay') });
  el.dataset.slideshowInit = '1';
});

// ── Toasts ───────────────────────────────────────────────

window.renlaceToast = function (message, kind) {
  const el = document.createElement('div');
  el.className = 'toast' + (kind === 'error' ? ' toast--error' : '');
  el.setAttribute('role', 'status');
  el.textContent = message;
  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-visible'));
  setTimeout(() => {
    el.classList.remove('is-visible');
    setTimeout(() => el.remove(), 300);
  }, 4000);
};

(window.renlaceToastQueue || []).forEach(args => window.renlaceToast(...args));
window.renlaceToastQueue = { push: args => window.renlaceToast(...args) };
